import React from "react";
import { Badge } from "./Badge";
import { Clock, AlertCircle, CheckCircle, XCircle } from "lucide-react";

export interface TimeRemainingProps {
  /** Timestamp when the item expires */
  expiresAt: number;
  /** Custom className for the container */
  className?: string;
  /** Show icon next to the time */
  showIcon?: boolean;
  /** Show status badge */
  showStatus?: boolean;
  /** Size variant */
  size?: "sm" | "md" | "lg";
}

/**
 * TimeRemaining - Displays remaining time until expiration with status indicators
 * Uses global Badge component for consistent branding
 */
export const TimeRemaining: React.FC<TimeRemainingProps> = ({
  expiresAt,
  className = "",
  showIcon = true,
  showStatus = true,
  size = "md",
}) => {
  const now = Date.now();
  const timeLeft = expiresAt - now;
  const daysLeft = timeLeft / (1000 * 60 * 60 * 24);

  // Calculate time remaining
  const getTimeRemaining = () => {
    if (timeLeft <= 0) return "Expired";

    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Get status information
  const getStatus = () => {
    if (timeLeft <= 0) {
      return {
        variant: "destructive" as const,
        icon: <XCircle className="h-4 w-4" />,
        label: "Expired",
      };
    }

    if (daysLeft < 10) {
      return {
        variant: "warning" as const,
        icon: <AlertCircle className="h-4 w-4" />,
        label: "Expiring Soon",
      };
    }

    return {
      variant: "success" as const,
      icon: <CheckCircle className="h-4 w-4" />,
      label: "Active",
    };
  };

  const timeText = getTimeRemaining();
  const status = getStatus();

  // Size variants
  const sizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  const iconSizes = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Time Display */}
      <div className={`flex items-center gap-1 ${sizeClasses[size]}`}>
        {showIcon && <Clock className={iconSizes[size]} />}
        <span className="font-mono text-slate-300">{timeText}</span>
      </div>

      {/* Status Badge */}
      {showStatus && (
        <Badge variant={status.variant} size={size}>
          {status.icon}
          <span className="ml-1">{status.label}</span>
        </Badge>
      )}
    </div>
  );
};

TimeRemaining.displayName = "TimeRemaining";
